import { useEffect, useRef } from 'react'
import type { MouseEvent, ReactElement, ReactNode } from 'react'

import styles from './modal.module.scss'

export type ModalProps = {
  /** Controlled by the caller: whether the dialog is on screen. */
  open: boolean
  /** Shown as the heading and used as the dialog's accessible name. */
  title: string
  /** Esc, a click on the backdrop, or the × — all three land here. */
  onClose: () => void
  children: ReactNode
  /** Panel width override, a CSS value (default 56rem). */
  maxWidth?: string
}

/**
 * The panel every dialog of the SPA renders into: focus moved inside on open,
 * Esc closes, a backdrop click closes. Presentational only — it knows nothing
 * about tRPC or the domain.
 */
export const Modal = ({ open, title, onClose, children, maxWidth }: ModalProps): ReactElement | null => {
  const panelRef = useRef<HTMLDivElement>(null)

  // Focus the panel on open, so Esc reaches it and the keyboard walks the
  // dialog's own controls instead of the page behind it.
  useEffect(() => {
    if (open) {
      panelRef.current?.focus()
    }
  }, [open])

  useEffect(() => {
    if (!open) {
      return
    }
    const handler = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose()
      }
    }
    document.addEventListener('keydown', handler)
    return () => { document.removeEventListener('keydown', handler) }
  }, [open, onClose])

  if (!open) {
    return null
  }

  const handleBackdropClick = (event: MouseEvent<HTMLDivElement>): void => {
    if (event.target === event.currentTarget) {
      onClose()
    }
  }

  return (
    <div className={styles.modal__backdrop} data-testid="modal-backdrop" onClick={handleBackdropClick}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={styles.modal__panel}
        style={maxWidth !== undefined ? { maxWidth } : undefined}
        tabIndex={-1}
      >
        <div className={styles.modal__header}>
          <h2 className={styles.modal__title}>{title}</h2>
          <button type="button" className={styles.modal__close} onClick={onClose} aria-label="Cerrar">
            {/* U+00D7 MULTIPLICATION SIGN — it looks like an ASCII "x" and is
                not one; do not "fix" it, the letter renders lopsided here. */}
            ×
          </button>
        </div>
        <div className={styles.modal__body}>{children}</div>
      </div>
    </div>
  )
}
